import React, { useState } from 'react';
import { ChevronDown, Loader2, UsersRound } from 'lucide-react';

// Renders the body of the "Usage by customer" accordion on the My Usage tab.
// Data comes from GET /agency/subscription/spend/customers (paginated,
// biggest spender first, each row carrying its package split).
//
// Props
//   customers        → response.data.customers
//   total            → response.data.total
//   page             → response.data.page (controlled)
//   totalPages       → response.data.total_pages
//   limit            → page size used for the request (rank numbers)
//   loading          → request in flight
//   onPageChange     → (page:number) => void
//   formatCents      → shared money formatter from MyPricingPlan
const UsageByCustomerPanel = ({
  customers = [],
  total = 0,
  page = 1,
  totalPages = 1,
  limit = 10,
  loading = false,
  onPageChange,
  formatCents,
}) => {
  const [expandedId, setExpandedId] = useState(null);

  const toggleRow = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const initialsOf = (name) => {
    const parts = String(name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
  };

  const rankOf = (idx) => (page - 1) * limit + idx + 1;
  const rangeStart = total ? (page - 1) * limit + 1 : 0;
  const rangeEnd = total ? Math.min(page * limit, total) : 0;

  const avgPerVerification = (c) =>
    c.verification_count ? Math.round(c.usage_cents / c.verification_count) : 0;

  const RankChip = ({ rank }) => (
    <span
      className={`inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
        rank === 1
          ? 'bg-gradient-to-br from-[#375AE7] to-[#4F6CFF] text-white shadow-sm shadow-blue-200'
          : 'bg-slate-100 text-slate-600'
      }`}
    >
      {rank}
    </span>
  );

  const PackageChips = ({ packages, size = 'text-xs' }) => (
    <div className="flex flex-wrap gap-1.5">
      {(packages || []).length ? (
        packages.map((pkg) => (
          <span
            key={pkg.code}
            className={`px-2 py-1 rounded-full bg-blue-50 text-[#375AE7] ${size} font-medium capitalize`}
          >
            {pkg.name || pkg.code} · {pkg.count ?? 0}
          </span>
        ))
      ) : (
        <span className="text-xs text-slate-400">—</span>
      )}
    </div>
  );

  // Package split for one customer — mirrors the "Usage by package" cards.
  const PackageSplit = ({ customer }) => (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          Package split
        </p>
        <p className="text-[11px] text-slate-500">
          {customer.verification_count ?? 0} verifications ·{' '}
          {formatCents(avgPerVerification(customer))} avg / verification
        </p>
      </div>
      {(customer.packages || []).length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3">
          {customer.packages.map((pkg) => (
            <div
              key={pkg.code}
              className="rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_4px_16px_-8px_rgba(15,23,42,0.12)]"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold text-slate-800 text-sm capitalize truncate">
                  {pkg.name || pkg.code}
                </p>
                <span className="text-sm font-bold text-slate-900 shrink-0">
                  {formatCents(pkg.spend_cents)}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                {pkg.count ?? 0} × {formatCents(pkg.unit_price_cents)}
              </p>
              <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#375AE7] to-[#4F6CFF]"
                  style={{
                    width: `${
                      customer.usage_cents
                        ? Math.min(
                            100,
                            ((pkg.spend_cents || 0) / customer.usage_cents) *
                              100,
                          )
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500">No package usage recorded.</p>
      )}
      <div className="mt-3 flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50/70 px-3 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-600">
          Customer total
        </span>
        <span className="text-sm font-bold text-emerald-700">
          {formatCents(customer.usage_cents)}
        </span>
      </div>
    </div>
  );

  if (loading && customers.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
        <Loader2 size={16} className="animate-spin text-[#375AE7]" />
        Loading customer usage…
      </div>
    );
  }

  if (!customers.length) {
    return (
      <div className="flex flex-col items-center justify-center py-8 border border-dashed border-slate-200 rounded-3xl">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center mb-3">
          <UsersRound size={22} className="text-violet-600" />
        </div>
        <h4 className="text-sm font-semibold text-slate-800">
          No customer usage this period
        </h4>
        <p className="text-xs text-slate-500 mt-1 text-center max-w-sm px-4">
          Verifications you run for customers will appear here, biggest spender
          first.
        </p>
      </div>
    );
  }

  return (
    <div className={loading ? 'opacity-60 pointer-events-none' : ''}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
          Biggest spender first
        </p>
        <p className="text-xs text-slate-500">
          Showing {rangeStart}–{rangeEnd} of {total} customers
        </p>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-sm text-slate-500 border-b">
              <th className="pb-3 w-12">#</th>
              <th className="pb-3">Customer</th>
              <th className="pb-3">Verifications</th>
              <th className="pb-3">Usage</th>
              <th className="pb-3">Packages</th>
              <th className="pb-3 w-10" />
            </tr>
          </thead>
          <tbody>
            {customers.map((c, idx) => {
              const isOpen = expandedId === c.customer_user_id;
              const rank = rankOf(idx);
              return (
                <React.Fragment key={c.customer_user_id}>
                  <tr
                    onClick={() => toggleRow(c.customer_user_id)}
                    aria-expanded={isOpen}
                    className={`border-b last:border-none cursor-pointer transition ${
                      isOpen ? 'bg-slate-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <td className="py-3">
                      <RankChip rank={rank} />
                    </td>
                    <td className="py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-[#375AE7]/15 to-[#4F6CFF]/15 text-[#375AE7] flex items-center justify-center text-sm font-semibold">
                          {initialsOf(c.customer_name)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 capitalize truncate">
                            {c.customer_name || 'Unnamed customer'}
                          </p>
                          <p className="text-xs text-slate-500">
                            {c.client_id || `ID #${c.customer_user_id}`}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 font-semibold text-slate-800">
                      {c.verification_count ?? 0}
                    </td>
                    <td className="py-3 font-semibold text-slate-900">
                      {formatCents(c.usage_cents)}
                    </td>
                    <td className="py-3">
                      <PackageChips packages={c.packages} />
                    </td>
                    <td className="py-3 text-right">
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white">
                        <ChevronDown
                          size={16}
                          className={`text-slate-500 transition-transform duration-300 ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </span>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b last:border-none">
                      <td colSpan={6} className="pb-4 pt-1">
                        <PackageSplit customer={c} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {customers.map((c, idx) => {
          const isOpen = expandedId === c.customer_user_id;
          const rank = rankOf(idx);
          return (
            <div
              key={c.customer_user_id}
              className="rounded-2xl border border-slate-200 p-3"
            >
              <button
                type="button"
                onClick={() => toggleRow(c.customer_user_id)}
                aria-expanded={isOpen}
                className="w-full flex items-center gap-3 text-left"
              >
                <div className="relative shrink-0">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#375AE7]/15 to-[#4F6CFF]/15 text-[#375AE7] flex items-center justify-center text-sm font-semibold">
                    {initialsOf(c.customer_name)}
                  </div>
                  <span className="absolute -top-1.5 -left-1.5 scale-90">
                    <RankChip rank={rank} />
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-800 capitalize truncate">
                    {c.customer_name || 'Unnamed customer'}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {c.client_id || `ID #${c.customer_user_id}`} ·{' '}
                    {c.verification_count ?? 0} verifications
                  </p>
                </div>
                <p className="font-semibold text-slate-900 text-sm shrink-0">
                  {formatCents(c.usage_cents)}
                </p>
                <ChevronDown
                  size={16}
                  className={`text-slate-400 shrink-0 transition-transform duration-300 ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {(c.packages || []).length > 0 && (
                <div className="mt-2.5">
                  <PackageChips packages={c.packages} size="text-[11px]" />
                </div>
              )}
              {isOpen && (
                <div className="mt-3">
                  <PackageSplit customer={c} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination — same controls as Invoices */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
          <button
            disabled={page === 1}
            onClick={() => onPageChange?.(page - 1)}
            className="px-4 py-2 rounded-xl border text-sm disabled:opacity-40 hover:bg-slate-50"
          >
            Previous
          </button>
          {[...Array(totalPages)].map((_, idx) => {
            const pg = idx + 1;
            return (
              <button
                key={pg}
                onClick={() => onPageChange?.(pg)}
                className={`w-10 h-10 rounded-xl text-sm font-medium ${
                  pg === page
                    ? 'bg-[#375AE7] text-white shadow'
                    : 'border hover:bg-slate-50'
                }`}
              >
                {pg}
              </button>
            );
          })}
          <button
            disabled={page === totalPages}
            onClick={() => onPageChange?.(page + 1)}
            className="px-4 py-2 rounded-xl border text-sm disabled:opacity-40 hover:bg-slate-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default UsageByCustomerPanel;
